import React from 'react';
import { Input, Checkbox } from '../../ui/form';
import { WorkflowWizardData } from '../workflow-wizard';

interface WorkflowBasicsStepProps {
  data: WorkflowWizardData;
  onChange: (updates: Partial<WorkflowWizardData>) => void;
  /** When true, the division has already forced "Require explicit Submit" on for every workflow - this checkbox is shown checked and disabled. */
  divisionRequireExplicitSubmit?: boolean;
}

export function WorkflowBasicsStep({ data, onChange, divisionRequireExplicitSubmit }: WorkflowBasicsStepProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Workflow Basics
        </h3>
        <p className="text-sm text-gray-600 dark:text-secondary-400 mb-6">
          Set up the basic information for your approval workflow
        </p>
      </div>

      <div className="space-y-4">
        <Input
          label="Workflow Name"
          value={data.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="e.g., Purchase Order Approval"
          required
        />
        <p className="mt-1 text-sm text-gray-500 dark:text-secondary-400">
          Choose a descriptive name that clearly identifies the purpose of this workflow
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
            Description
          </label>
          <textarea
            value={data.description}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="Describe when this workflow should be used..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
          />
          <p className="mt-1 text-sm text-gray-500 dark:text-secondary-400">
            Provide details about when and how this workflow should be applied
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
            Workflow Type
          </label>
          <select
            value={data.type}
            onChange={(e) => onChange({ type: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
          >
            <option value="approval">Approval Workflow</option>
            <option value="review">Review Workflow</option>
            <option value="procurement">Procurement Workflow</option>
            <option value="custom">Custom Workflow</option>
          </select>
          <p className="mt-1 text-sm text-gray-500 dark:text-secondary-400">
            Select the type that best describes this workflow's purpose
          </p>
        </div>

        <Input
          label="Default Step Timeout (hours)"
          type="number"
          min="1"
          value={data.defaultStepTimeoutHours?.toString() ?? ''}
          onChange={(e) => onChange({ defaultStepTimeoutHours: e.target.value ? parseInt(e.target.value) : undefined })}
          placeholder="Leave empty to use division default"
        />
        <p className="mt-1 text-sm text-gray-500 dark:text-secondary-400">
          Override the division's default step timeout for this workflow. Leave empty to use the division default.
        </p>

        <Input
          label="Pause Timeout (hours)"
          type="number"
          min="1"
          value={data.pauseTimeoutHours?.toString() ?? ''}
          onChange={(e) => onChange({ pauseTimeoutHours: e.target.value ? parseInt(e.target.value) : undefined })}
          placeholder="Leave empty to use division default"
        />
        <p className="mt-1 text-sm text-gray-500 dark:text-secondary-400">
          Override the division's default pause timeout for this workflow. Leave empty to use the division default.
        </p>

        <Input
          label="Default Timeout Warning (hours before)"
          type="number"
          min="1"
          value={data.defaultTimeoutWarningHours?.toString() ?? ''}
          onChange={(e) => onChange({ defaultTimeoutWarningHours: e.target.value ? parseInt(e.target.value) : undefined })}
          placeholder="Leave empty to use division default"
        />
        <p className="mt-1 text-sm text-gray-500 dark:text-secondary-400">
          Override the division's default timeout warning for this workflow. Leave empty to use the division default.
        </p>

        <div className="pt-4">
          <Checkbox
            label={`Require explicit "Submit" at every step (overrides individual step settings)${divisionRequireExplicitSubmit ? ' - enforced by division setting' : ''}`}
            checked={divisionRequireExplicitSubmit || (data.requireExplicitSubmit ?? false)}
            onChange={(e) => onChange({ requireExplicitSubmit: e.target.checked })}
            disabled={divisionRequireExplicitSubmit}
          />
        </div>

        <div className="pt-4">
          <Checkbox
            label="Activate workflow immediately after creation"
            checked={data.active}
            onChange={(e) => onChange({ active: e.target.checked })}
          />
          <p className="mt-1 ml-6 text-sm text-gray-500 dark:text-secondary-400">
            If unchecked, the workflow will be created in draft mode and can be activated later
          </p>
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h4 className="text-sm font-medium text-blue-900">
              Tip: Start with a clear workflow name
            </h4>
            <p className="mt-1 text-sm text-blue-700">
              A well-chosen name helps users quickly understand the workflow's purpose.
              Examples: "Standard PO Approval", "High-Value Purchase Review", "IT Equipment Procurement"
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
